import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  Calendar, 
  Clock, 
  Plus, 
  Edit, 
  Trash2, 
  CheckCircle, 
  XCircle,
  Save,
  X,
  AlertCircle
} from 'lucide-react';
import creneauxService, { Creneau } from '../../services/creneaux.service';
import { consultantsService } from '../../services/consultants.service';

interface CreateCreneauData {
  service_id: number;
  creneau_date: string;
  start_time: string;
  end_time: string;
  is_available: boolean;
  notes?: string;
}

const CreneauxManagementPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [creneaux, setCreneaux] = useState<Creneau[]>([]);
  const [pendingBookings, setPendingBookings] = useState<Creneau[]>([]);
  const [service, setService] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [editingCreneau, setEditingCreneau] = useState<number | null>(null);
  const [rejectingBooking, setRejectingBooking] = useState<number | null>(null);
  const [rejectionReason, setRejectionReason] = useState<string>('');
  const [deletingCreneau, setDeletingCreneau] = useState<number | null>(null);
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [formData, setFormData] = useState<CreateCreneauData>({
    service_id: parseInt(id || '0'),
    creneau_date: '',
    start_time: '',
    end_time: '',
    is_available: true,
    notes: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (id) {
      loadData();
    }
  }, [id]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [creneauxData, serviceData, pendingData] = await Promise.all([
        creneauxService.getAllCreneauxByService(id!),
        consultantsService.getService(id!),
        creneauxService.getMyPendingBookings()
      ]);
      setCreneaux(creneauxData);
      setService(serviceData);
      // Filter pending bookings for this specific service
      const servicePendingBookings = pendingData.filter(booking => booking.service_id === parseInt(id!));
      setPendingBookings(servicePendingBookings);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof CreateCreneauData, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.creneau_date) {
      newErrors.creneau_date = t('consultant.creneaux.errors.dateRequired', 'Date is required');
    }

    if (!formData.start_time) {
      newErrors.start_time = t('consultant.creneaux.errors.startTimeRequired', 'Start time is required');
    }

    if (!formData.end_time) {
      newErrors.end_time = t('consultant.creneaux.errors.endTimeRequired', 'End time is required');
    }

    if (formData.start_time && formData.end_time && formData.start_time >= formData.end_time) {
      newErrors.end_time = t('consultant.creneaux.errors.endTimeAfterStart', 'End time must be after start time');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCreateCreneau = async () => {
    if (!validateForm()) return;

    try {
      await creneauxService.createCreneau(formData);
      await loadData();
      resetForm();
      setIsCreating(false);
    } catch (error) {
    }
  };

  const handleUpdateCreneau = async (id: number) => {
    if (!validateForm()) return;

    try {
      await creneauxService.updateCreneau(id, formData);
      await loadData();
      resetForm();
      setEditingCreneau(null);
    } catch (error) {
    }
  };

  const handleDeleteCreneau = async (id: number) => {
    // Check if créneau is booked
    const creneauToDelete = creneaux.find(c => c.id === id);
    if (creneauToDelete?.is_booked) {
      setNotification({
        type: 'error',
        message: t('consultant.creneaux.errors.cannotDeleteBooked', 'Cannot delete a booked slot. Please cancel the booking first.')
      });
      setTimeout(() => setNotification(null), 5000);
      return;
    }

    if (!window.confirm(t('consultant.creneaux.confirmDelete', 'Are you sure you want to delete this slot?'))) return;

    try {
      setDeletingCreneau(id);
      await creneauxService.deleteCreneau(id);
      setNotification({
        type: 'success',
        message: t('consultant.creneaux.deleteSuccess', 'Slot deleted successfully')
      });
      setTimeout(() => setNotification(null), 3000);
      await loadData();
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || 
                          error?.message || 
                          t('consultant.creneaux.errors.deleteFailed', 'Failed to delete slot. Please try again.');
      setNotification({
        type: 'error',
        message: errorMessage
      });
      setTimeout(() => setNotification(null), 5000);
    } finally {
      setDeletingCreneau(null);
    }
  };

  const handleApproveBooking = async (creneauId: number) => {
    try {
      await creneauxService.approveBooking(creneauId);
      await loadData();
    } catch (error) {
    }
  };

  const handleRejectBooking = async (creneauId: number, reason?: string) => {
    try {
      await creneauxService.rejectBooking(creneauId, reason);
      await loadData();
      setRejectingBooking(null);
      setRejectionReason('');
    } catch (error) {
    }
  };

  const startRejectBooking = (creneauId: number) => {
    setRejectingBooking(creneauId);
    setRejectionReason('');
  };

  const cancelRejectBooking = () => {
    setRejectingBooking(null);
    setRejectionReason('');
  };

  const resetForm = () => {
    setFormData({
      service_id: parseInt(id || '0'),
      creneau_date: '',
      start_time: '',
      end_time: '',
      is_available: true,
      notes: ''
    });
    setErrors({});
  };

  const startEdit = (creneau: Creneau) => {
    setFormData({
      service_id: creneau.service_id,
      creneau_date: creneau.creneau_date,
      start_time: creneau.start_time,
      end_time: creneau.end_time,
      is_available: creneau.is_available,
      notes: creneau.notes || ''
    });
    setEditingCreneau(creneau.id);
  };

  const cancelEdit = () => {
    resetForm();
    setEditingCreneau(null);
    setIsCreating(false);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(i18n.language === 'fr' ? 'fr-FR' : 'en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Notification Toast */}
      {notification && (
        <div className={`fixed top-4 right-4 z-50 px-6 py-4 rounded-lg shadow-lg flex items-center gap-3 animate-slide-in ${
          notification.type === 'success' 
            ? 'bg-green-50 border border-green-200 text-green-800' 
            : 'bg-red-50 border border-red-200 text-red-800'
        }`}>
          {notification.type === 'success' ? (
            <CheckCircle className="h-5 w-5 text-green-600" />
          ) : (
            <XCircle className="h-5 w-5 text-red-600" />
          )}
          <span className="font-medium">{notification.message}</span>
          <button
            onClick={() => setNotification(null)}
            className="ml-4 text-gray-400 hover:text-gray-600"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('consultant.creneaux.title', 'Slot Management')}</h1>
          <p className="text-gray-600">
            {service ? `${t('consultant.creneaux.service', 'Service')}: ${service.title}` : t('consultant.creneaux.loading', 'Loading...')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate(`/consultant/services/${id}`)}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
          >
            <X className="h-4 w-4 mr-2" />
            {t('consultant.creneaux.backToService', 'Back to service')}
          </button>
          <button
            onClick={() => setIsCreating(true)}
            className="inline-flex items-center px-4 py-2 bg-purple-600 text-white font-medium rounded-lg hover:bg-purple-700 transition-colors"
          >
            <Plus className="h-4 w-4 mr-2" />
            {t('consultant.creneaux.newSlot', 'New slot')}
          </button>
        </div>
      </div>

      {/* Create/Edit Form */}
      {(isCreating || editingCreneau) && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            {isCreating ? t('consultant.creneaux.createNew', 'Create new slot') : t('consultant.creneaux.editSlot', 'Edit slot')}
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('consultant.creneaux.form.date', 'Date *')}
              </label>
              <input
                type="date"
                value={formData.creneau_date}
                onChange={(e) => handleInputChange('creneau_date', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                  errors.creneau_date ? 'border-red-300' : 'border-gray-300'
                }`}
                min={new Date().toISOString().split('T')[0]}
              />
              {errors.creneau_date && (
                <p className="mt-1 text-sm text-red-600">{errors.creneau_date}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('consultant.creneaux.form.startTime', 'Start time *')}
              </label>
              <input
                type="time"
                value={formData.start_time}
                onChange={(e) => handleInputChange('start_time', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                  errors.start_time ? 'border-red-300' : 'border-gray-300'
                }`}
              />
              {errors.start_time && (
                <p className="mt-1 text-sm text-red-600">{errors.start_time}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('consultant.creneaux.form.endTime', 'End time *')}
              </label>
              <input
                type="time"
                value={formData.end_time}
                onChange={(e) => handleInputChange('end_time', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                  errors.end_time ? 'border-red-300' : 'border-gray-300'
                }`}
              />
              {errors.end_time && (
                <p className="mt-1 text-sm text-red-600">{errors.end_time}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('consultant.creneaux.form.available', 'Available')}
              </label>
              <select
                value={formData.is_available.toString()}
                onChange={(e) => handleInputChange('is_available', e.target.value === 'true')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="true">{t('consultant.creneaux.form.availableYes', 'Available')}</option>
                <option value="false">{t('consultant.creneaux.form.availableNo', 'Unavailable')}</option>
              </select>
            </div>
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('consultant.creneaux.form.notes', 'Notes (optional)')}
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder={t('consultant.creneaux.form.notesPlaceholder', 'Additional notes...')}
            />
          </div>

          <div className="flex justify-end gap-2 mt-6">
            <button
              onClick={cancelEdit}
              className="px-4 py-2 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
            >
              {t('consultant.creneaux.actions.cancel', 'Cancel')}
            </button>
            <button
              onClick={() => isCreating ? handleCreateCreneau() : handleUpdateCreneau(editingCreneau!)}
              className="px-4 py-2 bg-purple-600 text-white font-medium rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2"
            >
              <Save className="h-4 w-4" />
              {isCreating ? t('consultant.creneaux.actions.create', 'Create') : t('consultant.creneaux.actions.save', 'Save')}
            </button>
          </div>
        </div>
      )}

      {/* Pending Bookings */}
      {pendingBookings.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-yellow-50">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-yellow-600" />
              {t('consultant.creneaux.pending.title', 'Pending requests')} ({pendingBookings.length})
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              {t('consultant.creneaux.pending.subtitle', 'Clients have requested to book these slots. Approve or reject their requests.')}
            </p>
          </div>
          
          <div className="divide-y divide-gray-200">
            {pendingBookings.map((booking) => (
              <div key={booking.id} className="p-6 hover:bg-gray-50 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <AlertCircle className="h-5 w-5 text-yellow-500" />
                    </div>
                    
                    <div>
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-gray-500" />
                          <span className="text-sm font-medium text-gray-900">
                            {new Date(booking.creneau_date).toLocaleDateString('fr-FR', {
                              weekday: 'long',
                              day: 'numeric',
                              month: 'long',
                              year: 'numeric'
                            })}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Clock className="h-4 w-4 text-gray-500" />
                          <span className="text-sm text-gray-600">
                            {booking.start_time} - {booking.end_time}
                          </span>
                        </div>
                      </div>
                      
                      {booking.trainee && (
                        <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                          <h4 className="text-sm font-semibold text-gray-900 mb-2">{t('consultant.creneaux.pending.requesterInfo', 'Requester information')}</h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                            <div>
                              <span className="font-medium text-gray-700">{t('consultant.creneaux.pending.name', 'Name')}:</span>
                              <span className="ml-2 text-gray-900">{booking.trainee.firstname} {booking.trainee.lastname}</span>
                            </div>
                            {booking.trainee.email && (
                              <div>
                                <span className="font-medium text-gray-700">{t('consultant.creneaux.pending.email', 'Email')}:</span>
                                <span className="ml-2 text-gray-900">{booking.trainee.email}</span>
                              </div>
                            )}
                            {booking.trainee.phone && (
                              <div>
                                <span className="font-medium text-gray-700">{t('consultant.creneaux.pending.phone', 'Phone')}:</span>
                                <span className="ml-2 text-gray-900">{booking.trainee.phone}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {booking.notes && (
                        <div className="mt-2 text-sm text-gray-600">
                          <strong>{t('consultant.creneaux.pending.notes', 'Notes')}:</strong> {booking.notes}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {rejectingBooking === booking.id ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={rejectionReason}
                          onChange={(e) => setRejectionReason(e.target.value)}
                          placeholder={t('consultant.creneaux.pending.rejectionReason', 'Rejection reason (optional)')}
                          className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-transparent"
                        />
                        <button
                          onClick={() => handleRejectBooking(booking.id, rejectionReason)}
                          className="px-3 py-1 bg-red-100 text-red-700 text-sm font-medium rounded-lg hover:bg-red-200 transition-colors flex items-center gap-1"
                        >
                          <XCircle className="h-4 w-4" />
                          {t('consultant.creneaux.pending.confirm', 'Confirm')}
                        </button>
                        <button
                          onClick={cancelRejectBooking}
                          className="px-3 py-1 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 transition-colors"
                        >
                          {t('consultant.creneaux.actions.cancel', 'Cancel')}
                        </button>
                      </div>
                    ) : (
                      <>
                        <button
                          onClick={() => startRejectBooking(booking.id)}
                          className="px-3 py-1 bg-red-100 text-red-700 text-sm font-medium rounded-lg hover:bg-red-200 transition-colors flex items-center gap-1"
                        >
                          <XCircle className="h-4 w-4" />
                          {t('consultant.creneaux.pending.reject', 'Reject')}
                        </button>
                        <button
                          onClick={() => handleApproveBooking(booking.id)}
                          className="px-3 py-1 bg-green-100 text-green-700 text-sm font-medium rounded-lg hover:bg-green-200 transition-colors flex items-center gap-1"
                        >
                          <CheckCircle className="h-4 w-4" />
                          {t('consultant.creneaux.pending.approve', 'Approve')}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Créneaux List */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            {t('consultant.creneaux.slots.title', 'Slots')} ({creneaux.length})
          </h3>
        </div>

        {creneaux.length === 0 ? (
          <div className="p-8 text-center">
            <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">{t('consultant.creneaux.slots.none', 'No slots created')}</h3>
            <p className="text-gray-600 mb-4">
              {t('consultant.creneaux.slots.empty', 'Create your first slot to allow clients to book appointments.')}
            </p>
            <button
              onClick={() => setIsCreating(true)}
              className="inline-flex items-center px-4 py-2 bg-purple-600 text-white font-medium rounded-lg hover:bg-purple-700 transition-colors"
            >
              <Plus className="h-4 w-4 mr-2" />
              {t('consultant.creneaux.slots.create', 'Create a slot')}
            </button>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {creneaux.map((creneau) => (
              <div key={creneau.id} className="p-6 hover:bg-gray-50 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      {creneau.is_booked ? (
                        <XCircle className="h-5 w-5 text-red-500" />
                      ) : creneau.is_available ? (
                        <CheckCircle className="h-5 w-5 text-green-500" />
                      ) : (
                        <AlertCircle className="h-5 w-5 text-gray-400" />
                      )}
                    </div>
                    
                    <div>
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-gray-400" />
                          <span className="font-medium text-gray-900">
                            {formatDate(creneau.creneau_date)}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Clock className="h-4 w-4 text-gray-400" />
                          <span className="text-gray-600">
                            {creneau.start_time} - {creneau.end_time}
                          </span>
                        </div>
                      </div>
                      
                      {creneau.is_booked && creneau.trainee && (
                        <div className="mt-2 text-sm text-gray-600">
                          {t('consultant.creneaux.slots.bookedBy', 'Booked by')}: {creneau.trainee.firstname} {creneau.trainee.lastname}
                          {creneau.trainee.email && ` (${creneau.trainee.email})`}
                        </div>
                      )}
                      
                      {creneau.notes && (
                        <div className="mt-2 text-sm text-gray-600">
                          {t('consultant.creneaux.slots.notes', 'Notes')}: {creneau.notes}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {!creneau.is_booked ? (
                      <>
                        <button
                          onClick={() => startEdit(creneau)}
                          disabled={editingCreneau !== null || deletingCreneau !== null}
                          className="p-2 text-gray-400 hover:text-purple-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          title={t('consultant.creneaux.actions.edit', 'Edit slot')}
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteCreneau(creneau.id)}
                          disabled={(deletingCreneau !== null && deletingCreneau !== creneau.id) || editingCreneau !== null}
                          className="p-2 text-gray-400 hover:text-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed relative"
                          title={t('consultant.creneaux.actions.delete', 'Delete slot')}
                        >
                          {deletingCreneau === creneau.id ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-red-600 border-t-transparent"></div>
                          ) : (
                            <Trash2 className="h-4 w-4" />
                          )}
                        </button>
                      </>
                    ) : (
                      <div className="px-3 py-1 bg-gray-100 text-gray-500 text-sm rounded-lg">
                        {t('consultant.creneaux.booked', 'Booked')}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CreneauxManagementPage;
